/**
 * Parameters for configuring design materials at runtime.
 * @module
 */
import type { DesignMaterialParamsPreprocessed } from "./designMaterialTypes";

export enum DetailRenderMode {
  SDF = 0,
  ParallaxMap = 1,
}

export const DEFAULT_DETAIL_RENDER_MODE = DetailRenderMode.ParallaxMap;
export const MAIN_MASK_PREFIX = "mainMask__";

// These names have been used in filenames, so we disable the naming convention
/* eslint-disable @typescript-eslint/naming-convention */
export enum ComponentViewMode {
  // Layer based modes:
  finalMaterial = 0,
  color = 1,
  roughness = 2,
  metalness = 3,
  textureHeight = 4,

  // Single layer modes:
  rawLayerColor = 5,
  rawLayerRoughness = 6,
  rawLayerMetalness = 7,
  rawLayerHeight = 8,
  layerMask = 9,
  rawLayerColorInfluence = 10,

  layerSample = 11,

  // Non-layer based modes:
  SDFMask = 13,
  RandomMask = 14,
  light = 15,
  normalMapOnly = 16,
  mapNormal = 17,

  // Outputs the distance between the flat material surface and the traced SDF surface. Normalized to the SDF depth parameter.
  // A value of 0.5 corresponds to the material surface, and 1.0 corresponds to SDF depth below the material surface.
  // Values < 0.5 (SDF protruding outside the material surface) are also possible.
  // If rendered head-on with an orthographic camera, this can be used to convert the SDF to a height map.
  SDFDistanceFromSurface = 18,

  detailGeometryNormal = 19,

  UV = 20,

  UVWithParallax = 21,

  edgeWearMask = 22, // Shows the raw edge wear mask based on the SDF pattern.

  bakedColor = 23, // To be baked into a color texture that can be used with a simpler material shader.
}
/* eslint-enable @typescript-eslint/naming-convention */

export function isComponentViewModeSingleLayer(componentViewMode: ComponentViewMode) {
  return componentViewMode >= ComponentViewMode.rawLayerColor && componentViewMode <= ComponentViewMode.layerSample;
}

export function isComponentViewModeLayerBased(componentViewMode: ComponentViewMode) {
  return componentViewMode < ComponentViewMode.SDFMask;
}

// If LayerViewMode is other than ALL_LAYERS, the rendering is done as if only that layer is enabled.
export enum LayerViewMode {
  ALL_LAYERS = 0,
  LAYER0 = 1,
  LAYER1 = 2,
  LAYER2 = 3,
  LAYER3 = 4,
}

export enum BakeColorTextureMode {
  DONT_BAKE = "DONT_BAKE",
  REPEATING_LAYERS = "REPEATING_LAYERS", // Makes the random variability in layers repeat regularly.
  BAKE = "BAKE", // For experimental use only. This will dynamically bake the color texture which is extremely slow.
  FLAT_COLOR = "FLAT_COLOR",
  FETCH_FROM_AWS = "FETCH_FROM_AWS", // This will fetch the color texture from AWS.
}

export interface DesignMaterialLoadOptions {
  disableSDF: boolean;
  disableLayers: boolean;
  enableLightOffsetMap: boolean;
  offsetMapLightCount: number;
  repeatingSDF: boolean; // Makes the random variability in the SDF pattern repeat regularly every 50 inches.
  forceDetailRenderMode?: DetailRenderMode;
  layerViewMode: LayerViewMode;
  componentViewMode: ComponentViewMode;
  useWebgl2TextureAtlas: boolean;
  reproduceImplicitLodIntelBug: boolean; // This option is intended to reproduce a bug on Intel GPU drivers.
  bakeColorTexture: BakeColorTextureMode;
}

export const defaultDesignMaterialLoadOptions: DesignMaterialLoadOptions = {
  disableSDF: false,
  disableLayers: false,
  enableLightOffsetMap: true,
  offsetMapLightCount: 8,
  repeatingSDF: false,
  forceDetailRenderMode: undefined,
  layerViewMode: LayerViewMode.ALL_LAYERS,
  componentViewMode: ComponentViewMode.finalMaterial,
  useWebgl2TextureAtlas: true,
  reproduceImplicitLodIntelBug: false,
  bakeColorTexture: BakeColorTextureMode.DONT_BAKE,
};

export type MaterialParamsWithoutSdf = Omit<DesignMaterialParamsPreprocessed, "sdf">;
